// Executive Growth Tool — the Growth Command Centre, rebuilt natively in the Game Plan Tracker design system.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Cloud, CloudOff, Loader2, Printer, RotateCcw, Maximize2, Minimize2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBranding, brandLine } from '../hooks/useBranding';
import { cn } from '../lib/utils';
import { blankState, normalise, compute } from '../components/growth/growthData';
import CommandTab from '../components/growth/CommandTab';
import TimelineTab from '../components/growth/TimelineTab';
import ReverseTab from '../components/growth/ReverseTab';
import { ScenariosTab, RecruitTab } from '../components/growth/PlanTables';
import DeptsTab from '../components/growth/DeptsTab';
import { TasksTab, DeliveryTab } from '../components/growth/TasksDelivery';
import '../components/gameplan/jarvis/jarvis.css';
import '../components/growth/growth.css';

const API = process.env.REACT_APP_BACKEND_URL;
const NAV = [
  { key: 'command', label: 'Command Centre', mobile: 'Command' }, { key: 'timeline', label: 'Growth Timeline', mobile: 'Timeline' }, { key: 'reverse', label: 'Reverse-Engineer', mobile: 'Reverse' },
  { key: 'scenarios', label: 'Scenarios', mobile: 'Scenarios' }, { key: 'recruit', label: 'Recruitment', mobile: 'Hiring' }, { key: 'depts', label: 'Departments', mobile: 'Depts' },
  { key: 'tasks', label: 'Tasks', mobile: 'Tasks' }, { key: 'delivery', label: 'Delivery Plan', mobile: 'Delivery' },
];

const Clock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 10_000); return () => clearInterval(id); }, []);
  return <span className="gpj-pill" data-testid="egt-clock">{now.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })} · {now.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}</span>;
};

const Tabs = ({ active, onChange }) => {
  const wrap = useRef(null);
  const [ind, setInd] = useState({ x: 4, w: 0 });
  useLayoutEffect(() => { const el = wrap.current?.querySelector(`[data-tabkey="${active}"]`); if (el) { setInd({ x: el.offsetLeft, w: el.offsetWidth }); el.scrollIntoView?.({ inline: 'nearest', block: 'nearest' }); } }, [active]);
  return (
    <nav className="gpj-tabs" role="tablist" ref={wrap} data-testid="egt-nav">
      <span className="gpj-tab-ind" style={{ transform: `translateX(${ind.x}px)`, width: ind.w }} />
      {NAV.map(({ key, label, mobile }) => (
        <button key={key} role="tab" aria-selected={active === key} className="gpj-tab" data-tabkey={key} onClick={() => onChange(key)} data-testid={`egt-tab-btn-${key}`}>
          <span className="hidden sm:inline">{label}</span><span className="sm:hidden">{mobile}</span>
        </button>
      ))}
    </nav>
  );
};

export default function ExecutiveGrowthTool() {
  const { token } = useAuth();
  const brand = useBranding();
  const [S, setS] = useState(null);
  const [tab, setTab] = useState('command');
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [live, setLive] = useState(null);
  const [liveErr, setLiveErr] = useState(null);
  const [full, setFull] = useState(false);
  const timer = useRef(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/growth-tool/state`, { headers }).then((r) => { setS(normalise(r.data.state)); setSaved(r.data.updated_at); })
      .catch(() => { setS(blankState()); toast.error('Could not load your saved growth plan — starting fresh'); });
  }, [token, headers]);

  // Pick up teammates' changes when the tab comes back into view (skipped while a save is pending).
  useEffect(() => {
    if (!token) return undefined;
    const onVis = async () => {
      if (document.hidden || timer.current) return;
      try { const { data } = await axios.get(`${API}/api/growth-tool/state`, { headers }); if (data.updated_at && data.updated_at !== saved) { setS(normalise(data.state)); setSaved(data.updated_at); } } catch { /* keep current */ }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [token, headers, saved]);

  const persist = useCallback((next) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaving(true);
      try { const { data } = await axios.put(`${API}/api/growth-tool/state`, { state: next }, { headers }); setSaved(data.updated_at); }
      catch { toast.error('Could not save your changes to the cloud'); }
      finally { setSaving(false); timer.current = null; }
    }, 600);
  }, [headers]);
  const update = useCallback((patch) => setS((prev) => { const next = { ...prev, ...patch }; persist(next); return next; }), [persist]);
  const toggleTask = useCallback((t) => setS((prev) => { const next = { ...prev, tasks: { ...prev.tasks, [t.name]: !prev.tasks[t.name] } }; persist(next); return next; }), [persist]);

  const fetchLive = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/growth-tool/live`, { headers }); setLive(data); setLiveErr(null); }
    catch (e) { setLiveErr(e?.response?.data?.detail || 'Could not reach the Game Plan Tracker'); }
  }, [headers]);
  useEffect(() => {
    if (!token || S?.mode !== 'live') return undefined;
    fetchLive();
    const id = setInterval(() => { if (!document.hidden) fetchLive(); }, 30_000);
    return () => clearInterval(id);
  }, [token, S?.mode, fetchLive]);

  const D = useMemo(() => (S ? compute(S, live) : null), [S, live]);

  return (
    <div className={cn('min-h-screen', full && 'fixed inset-0 z-[90] overflow-auto')} style={{ background: '#04060a' }} data-testid="executive-growth-tool">
      <div className="gpj egt">
        <div className="gpj-ambient" aria-hidden="true"><i /><i /><i /><b /></div>
        <div className="gpj-shell max-w-[1520px] mx-auto" style={{ paddingInline: 'clamp(16px,3vw,40px)' }}>
          <header className="gpj-top">
            <div className="gpj-brand"><small>Growth Command</small><strong>Executive Growth Tool</strong></div>
            <Link to="/" className="gpj-pill" style={{ textDecoration: 'none' }} data-testid="egt-back-home">← Derwent AI</Link>
            <span className={cn('gpj-pill', S?.mode !== 'live' && 'stale')} data-testid="egt-mode-pill"><span className="gpj-live-dot" style={S?.mode !== 'live' ? { background: 'var(--amber)', animation: 'none' } : undefined} />{S?.mode === 'live' ? 'Live · Game Plan' : S?.mode === 'example' ? 'Example figures' : 'Manual actuals'}</span>
            <Clock />
            <span className="gpj-pill" data-testid="egt-save-status">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saved ? <Cloud className="w-3.5 h-3.5" style={{ color: 'var(--green)' }} /> : <CloudOff className="w-3.5 h-3.5" />}
              <span className="hidden md:inline">{saving ? 'Saving…' : saved ? `Saved ${new Date(saved).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}` : 'Autosaves to workspace'}</span>
            </span>
            <button type="button" className="gpj-btn" onClick={() => { if (window.confirm('Reset assumptions, actuals, headcount and task progress to defaults?')) update(blankState()); }} data-testid="egt-reset-all"><RotateCcw /> <span className="hidden sm:inline">Reset plan</span></button>
            <button type="button" className="gpj-btn icon" onClick={() => setFull((f) => !f)} title={full ? 'Exit full screen' : 'Full screen'} data-testid="egt-fullscreen">{full ? <Minimize2 /> : <Maximize2 />}</button>
            <button type="button" className="gpj-btn primary" onClick={() => window.print()} data-testid="egt-export"><Printer /> <span className="hidden sm:inline">Export report</span></button>
          </header>

          <main className="pb-28">
            <Tabs active={tab} onChange={(k) => { setTab(k); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
            {!S ? <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--fg-3)' }} /></div> : (
              <div key={tab} className="gpj-view">
                {tab === 'command' && <CommandTab S={S} D={D} live={live} liveErr={liveErr} update={update} />}
                {tab === 'timeline' && <TimelineTab S={S} onToggle={toggleTask} />}
                {tab === 'reverse' && <ReverseTab S={S} D={D} update={update} />}
                {tab === 'scenarios' && <ScenariosTab S={S} />}
                {tab === 'recruit' && <RecruitTab />}
                {tab === 'depts' && <DeptsTab />}
                {tab === 'tasks' && <TasksTab S={S} update={update} onToggle={toggleTask} />}
                {tab === 'delivery' && <DeliveryTab />}
              </div>
            )}
            <div className="gpj-foot"><span data-testid="egt-brand-line">{brandLine(brand) || 'DerwentAI'}</span><span>Planning projections are labelled · live figures come straight from your workspace</span></div>
          </main>
        </div>
      </div>
    </div>
  );
}
